import fs from 'node:fs';
import path from 'node:path';
import {
  AnalysisCaches,
  BarrelAnalysis,
  ManualAliasMapping,
  Options,
  ReExportTarget,
  ResolutionCaches,
  TsconfigInfo,
} from './types';
import { parseBarrelFile } from './analysis';
import {
  applyAliasTarget,
  buildAliasSpecifier,
  getOptionsCacheKey,
  isRelativePath,
  matchesAliasPattern,
  normalizeModulePath,
  resolveModuleFile,
  toRelativeImportSpecifier,
} from './path-utils';
import type typescriptModule from 'typescript';

type TypeScriptModule = typeof typescriptModule;

let loadTypeScriptModule: () => TypeScriptModule | null = () => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('typescript') as TypeScriptModule;
  } catch {
    /* c8 ignore next */
    return null;
  }
};

const sharedTsconfigCache = new Map<string, TsconfigInfo | null>();
const sharedTsconfigPathCache = new Map<string, string | null>();
const sharedBarrelAnalysisCache = new Map<string, BarrelAnalysis | null>();

export function createResolutionCaches(
  sharedTsconfigCache: Map<string, TsconfigInfo | null> = new Map<string, TsconfigInfo | null>(),
  sharedTsconfigPathCache: Map<string, string | null> = new Map<string, string | null>(),
): ResolutionCaches {
  return {
    barrelAnalyses: new Map<string, BarrelAnalysis | null>(),
    importResolutions: new Map<string, string | null>(),
    tsconfigInfo: sharedTsconfigCache,
    tsconfigPaths: sharedTsconfigPathCache,
  };
}

export function createSharedResolutionCaches(): ResolutionCaches {
  return {
    ...createResolutionCaches(sharedTsconfigCache, sharedTsconfigPathCache),
    barrelAnalyses: sharedBarrelAnalysisCache,
  };
}

export function setTypeScriptModuleLoaderForTests(loader: (() => TypeScriptModule | null) | null): void {
  sharedTsconfigCache.clear();
  sharedTsconfigPathCache.clear();
  sharedBarrelAnalysisCache.clear();

  loadTypeScriptModule =
    loader ??
    (() => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        return require('typescript') as TypeScriptModule;
      } catch {
        /* c8 ignore next */
        return null;
      }
    });
}

export function getTypeScriptModule(): TypeScriptModule | null {
  return loadTypeScriptModule();
}

export function hasTypeScriptModule(): boolean {
  return getTypeScriptModule() !== null;
}

export function getTsconfigPath(
  options: Options[0] | undefined,
  importerFilename: string,
  cwd: string = process.cwd(),
  tsconfigPathCache: Map<string, string | null> = new Map<string, string | null>(),
): string | null {
  const typescript = getTypeScriptModule();

  if (!typescript) {
    return null;
  }

  if (options?.tsconfig === false) {
    return null;
  }

  if (typeof options?.tsconfig === 'string') {
    return path.isAbsolute(options.tsconfig) ? options.tsconfig : path.resolve(cwd, options.tsconfig);
  }

  const importerDirectory = path.dirname(importerFilename);
  const cacheKey = `${getOptionsCacheKey(options, cwd)}\0${importerDirectory}`;
  const cachedPath = tsconfigPathCache.get(cacheKey);

  if (cachedPath !== undefined) {
    return cachedPath;
  }

  const tsconfigPath = typescript.findConfigFile(importerDirectory, typescript.sys.fileExists, 'tsconfig.json') ?? null;
  tsconfigPathCache.set(cacheKey, tsconfigPath);

  return tsconfigPath;
}

export function getTsconfigInfo(
  options: Options[0] | undefined,
  importerFilename: string,
  tsconfigCache: Map<string, TsconfigInfo | null>,
  cwd: string = process.cwd(),
  tsconfigPathCache: Map<string, string | null> = new Map<string, string | null>(),
): TsconfigInfo | null {
  const typescript = getTypeScriptModule();

  if (!typescript) {
    return null;
  }

  const tsconfigPath = getTsconfigPath(options, importerFilename, cwd, tsconfigPathCache);

  if (!tsconfigPath) {
    return null;
  }

  if (!tsconfigCache.has(tsconfigPath)) {
    const configFile = typescript.readConfigFile(tsconfigPath, typescript.sys.readFile);

    if (configFile.error) {
      tsconfigCache.set(tsconfigPath, null);
    } else {
      const parsedConfig = typescript.parseJsonConfigFileContent(
        configFile.config,
        typescript.sys,
        path.dirname(tsconfigPath),
        undefined,
        tsconfigPath,
      );

      tsconfigCache.set(tsconfigPath, {
        compilerOptions: parsedConfig.options,
        configFilePath: tsconfigPath,
        moduleResolutionCache: typescript.createModuleResolutionCache(
          path.dirname(tsconfigPath),
          typescript.sys.useCaseSensitiveFileNames ? fileName => fileName : fileName => fileName.toLowerCase(),
          parsedConfig.options,
        ),
      });
    }
  }

  return tsconfigCache.get(tsconfigPath) ?? null;
}

export function resolveWithManualPaths(
  options: Options[0] | undefined,
  importerFilename: string,
  specifier: string,
  cwd: string = process.cwd(),
): string | null {
  const configuredPaths = options?.paths;

  if (!configuredPaths) {
    return null;
  }

  for (const [pattern, targetValue] of Object.entries(configuredPaths)) {
    const wildcardValue = matchesAliasPattern(specifier, pattern);

    if (wildcardValue === null) {
      continue;
    }

    const targets = Array.isArray(targetValue) ? targetValue : [targetValue];

    for (const target of targets) {
      const candidateSpecifier = applyAliasTarget(target, wildcardValue);
      const candidateBasePath = path.resolve(cwd, candidateSpecifier);
      const resolvedFilePath = resolveModuleFile(importerFilename, candidateBasePath);

      if (resolvedFilePath) {
        return resolvedFilePath;
      }
    }
  }

  return null;
}

function resolveWithBaseUrl(importerFilename: string, specifier: string, tsconfigInfo: TsconfigInfo): string | null {
  const baseUrl = tsconfigInfo.compilerOptions.baseUrl;

  if (!baseUrl) {
    return null;
  }

  const configDirectory = path.dirname(tsconfigInfo.configFilePath);
  const baseUrlDirectory = path.isAbsolute(baseUrl) ? baseUrl : path.resolve(configDirectory, baseUrl);

  return resolveModuleFile(importerFilename, path.resolve(baseUrlDirectory, specifier));
}

function hasMatchingTsconfigPath(specifier: string, tsconfigInfo: TsconfigInfo): boolean {
  const paths = tsconfigInfo.compilerOptions.paths;

  return (
    !!paths &&
    typeof paths === 'object' &&
    Object.keys(paths).some(pattern => matchesAliasPattern(specifier, pattern) !== null)
  );
}

export function getManualAliasMappings(options: Options[0] | undefined): Array<ManualAliasMapping> {
  const configuredPaths = options?.paths;

  if (!configuredPaths) {
    return [];
  }

  return Object.entries(configuredPaths).flatMap(([pattern, targetValue]) =>
    (Array.isArray(targetValue) ? targetValue : [targetValue]).map(target => ({
      pattern,
      target,
    })),
  );
}

export function resolveWithTsconfig(
  options: Options[0] | undefined,
  importerFilename: string,
  specifier: string,
  tsconfigCache: Map<string, TsconfigInfo | null>,
  cwd: string = process.cwd(),
  tsconfigPathCache: Map<string, string | null> = new Map<string, string | null>(),
): string | null {
  const typescript = getTypeScriptModule();

  if (!typescript) {
    return null;
  }

  const tsconfigInfo = getTsconfigInfo(options, importerFilename, tsconfigCache, cwd, tsconfigPathCache);

  if (!tsconfigInfo) {
    return null;
  }

  const baseUrlResolution = resolveWithBaseUrl(importerFilename, specifier, tsconfigInfo);

  if (baseUrlResolution) {
    return baseUrlResolution;
  }

  if (!hasMatchingTsconfigPath(specifier, tsconfigInfo)) {
    return null;
  }

  const resolvedModule = typescript.resolveModuleName(
    specifier,
    importerFilename,
    tsconfigInfo.compilerOptions,
    typescript.sys,
    tsconfigInfo.moduleResolutionCache,
  ).resolvedModule;

  if (!resolvedModule || resolvedModule.isExternalLibraryImport) {
    return null;
  }

  return fs.existsSync(resolvedModule.resolvedFileName) ? resolvedModule.resolvedFileName : null;
}

export function resolveImport(
  options: Options[0] | undefined,
  resolutionCaches: ResolutionCaches,
  importerFilename: string,
  specifier: string,
  cwd: string = process.cwd(),
): string | null {
  const cacheKey = `${getOptionsCacheKey(options, cwd)}\0${importerFilename}\0${specifier}`;
  const cachedResult = resolutionCaches.importResolutions.get(cacheKey);

  if (cachedResult !== undefined) {
    return cachedResult;
  }

  const resolvedFilePath = isRelativePath(specifier)
    ? resolveModuleFile(importerFilename, specifier)
    : (resolveWithManualPaths(options, importerFilename, specifier, cwd) ??
      resolveWithTsconfig(
        options,
        importerFilename,
        specifier,
        resolutionCaches.tsconfigInfo,
        cwd,
        resolutionCaches.tsconfigPaths,
      ));

  resolutionCaches.importResolutions.set(cacheKey, resolvedFilePath);

  return resolvedFilePath;
}

export function reverseResolveManualAlias(
  options: Options[0] | undefined,
  resolvedFilePath: string,
  cwd: string = process.cwd(),
): string | null {
  const candidateAliases = getManualAliasMappings(options)
    .map(mapping => {
      if (mapping.target.includes('*')) {
        const [rawPrefix = '', rawSuffix = ''] = mapping.target.split('*');
        const normalizedResolvedFilePath = normalizeModulePath(resolvedFilePath);
        const normalizedPrefix = normalizeModulePath(path.resolve(cwd, rawPrefix));
        const normalizedSuffix = normalizeModulePath(rawSuffix);

        if (
          !normalizedResolvedFilePath.startsWith(normalizedPrefix) ||
          !normalizedResolvedFilePath.endsWith(normalizedSuffix)
        ) {
          return null;
        }

        const wildcardValue = normalizedResolvedFilePath.slice(
          normalizedPrefix.length,
          normalizedResolvedFilePath.length - normalizedSuffix.length,
        );

        return buildAliasSpecifier(mapping.pattern, wildcardValue.replace(/^\//, ''));
      }

      const targetFilePath = resolveModuleFile(cwd, path.resolve(cwd, mapping.target));

      return targetFilePath === resolvedFilePath ? mapping.pattern : null;
    })
    .filter((value): value is string => value !== null);

  return candidateAliases.length === 1 ? candidateAliases[0]! : null;
}

export function reverseResolveTsconfigAlias(
  options: Options[0] | undefined,
  importerFilename: string,
  resolvedFilePath: string,
  tsconfigCache: Map<string, TsconfigInfo | null>,
  cwd: string = process.cwd(),
  tsconfigPathCache: Map<string, string | null> = new Map<string, string | null>(),
): string | null {
  const tsconfigInfo = getTsconfigInfo(options, importerFilename, tsconfigCache, cwd, tsconfigPathCache);

  if (!tsconfigInfo) {
    return null;
  }

  const paths = tsconfigInfo.compilerOptions.paths;

  if (!paths || typeof paths !== 'object') {
    return null;
  }

  const configDirectory = path.dirname(tsconfigInfo.configFilePath);
  const baseUrl = tsconfigInfo.compilerOptions.baseUrl;
  const pathsBaseDirectory = baseUrl
    ? path.isAbsolute(baseUrl)
      ? baseUrl
      : path.resolve(configDirectory, baseUrl)
    : configDirectory;

  const candidateAliases = Object.entries(paths as Record<string, string[]>)
    .flatMap(([pattern, targets]) =>
      targets.map(target => {
        if (target.includes('*')) {
          const [rawPrefix = '', rawSuffix = ''] = target.split('*');
          const normalizedResolvedFilePath = normalizeModulePath(resolvedFilePath);
          const normalizedPrefix = normalizeModulePath(path.resolve(pathsBaseDirectory, rawPrefix));
          const normalizedSuffix = normalizeModulePath(rawSuffix);

          if (
            !normalizedResolvedFilePath.startsWith(normalizedPrefix) ||
            !normalizedResolvedFilePath.endsWith(normalizedSuffix)
          ) {
            return null;
          }

          const wildcardValue = normalizedResolvedFilePath.slice(
            normalizedPrefix.length,
            normalizedResolvedFilePath.length - normalizedSuffix.length,
          );

          return buildAliasSpecifier(pattern, wildcardValue.replace(/^\//, ''));
        }

        const targetFilePath = resolveModuleFile(importerFilename, path.resolve(pathsBaseDirectory, target));

        return targetFilePath === resolvedFilePath ? pattern : null;
      }),
    )
    .filter((value): value is string => value !== null);

  return candidateAliases.length === 1 ? candidateAliases[0]! : null;
}

export function getPreferredSourceSpecifier(
  options: Options[0] | undefined,
  importerFilename: string,
  originalImportSpecifier: string,
  reExportTarget: ReExportTarget,
  tsconfigCache: Map<string, TsconfigInfo | null>,
  cwd: string = process.cwd(),
  tsconfigPathCache: Map<string, string | null> = new Map<string, string | null>(),
): string | null {
  const fixStyle = options?.fixStyle ?? 'auto';

  if (fixStyle === 'relative') {
    return toRelativeImportSpecifier(importerFilename, reExportTarget.resolvedFilePath);
  }

  if (!isRelativePath(reExportTarget.sourceSpecifier)) {
    return reExportTarget.sourceSpecifier;
  }

  const aliasCandidates = [
    reverseResolveManualAlias(options, reExportTarget.resolvedFilePath, cwd),
    reverseResolveTsconfigAlias(
      options,
      importerFilename,
      reExportTarget.resolvedFilePath,
      tsconfigCache,
      cwd,
      tsconfigPathCache,
    ),
  ].filter((value): value is string => value !== null);
  const uniqueAliasCandidates = Array.from(new Set(aliasCandidates));

  if (fixStyle === 'preserve-alias') {
    return uniqueAliasCandidates.length === 1 ? uniqueAliasCandidates[0]! : null;
  }

  if (!isRelativePath(originalImportSpecifier) && uniqueAliasCandidates.length === 1) {
    return uniqueAliasCandidates[0]!;
  }

  return toRelativeImportSpecifier(importerFilename, reExportTarget.resolvedFilePath);
}

export function getBarrelAnalysis(
  options: Options[0] | undefined,
  barrelFilePath: string,
  barrelExportCache: Map<string, BarrelAnalysis | null>,
  resolutionCaches: ResolutionCaches,
  analysisCaches: AnalysisCaches,
  cwd: string = process.cwd(),
): BarrelAnalysis | null {
  const cacheKey = `${getOptionsCacheKey(options, cwd)}\0${barrelFilePath}`;
  const cachedAnalysis = barrelExportCache.get(cacheKey);

  if (cachedAnalysis !== undefined) {
    return cachedAnalysis;
  }

  let barrelAnalysis = resolutionCaches.barrelAnalyses.get(cacheKey);

  if (!resolutionCaches.barrelAnalyses.has(cacheKey)) {
    barrelAnalysis = parseBarrelFile(
      barrelFilePath,
      (importerFilename, specifier) => resolveImport(options, resolutionCaches, importerFilename, specifier, cwd),
      analysisCaches,
    );
    resolutionCaches.barrelAnalyses.set(cacheKey, barrelAnalysis);
  }

  barrelExportCache.set(cacheKey, barrelAnalysis ?? null);

  return barrelAnalysis ?? null;
}
